import { DragDropModule } from '@angular/cdk/drag-drop';
import { CloseScrollStrategy, Overlay, OverlayModule, RepositionScrollStrategy } from '@angular/cdk/overlay';
import { CommonModule, DatePipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CodeInputModule } from 'angular-code-input';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { EllipsisModule } from 'ngx-ellipsis';
import { NgScrollbarModule, NG_SCROLLBAR_OPTIONS, provideScrollbarOptions } from 'ngx-scrollbar';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule, MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { BaseWarningModalComponent } from '../components/base-warning-modal/base-warning-modal.component';
import { DisableCopyPasteDirective } from '../directives/disable-copy-paste.directive';
import { ExtendedRouterLinkDirective } from '../directives/extended-router-link.directive';
import { HighlightDirective } from '../directives/hightlight.directive';
import { TripleClickDirective } from '../directives/triple-click.directive';
import { DisableControlDirective } from '../model/directives/disable-control.directive';
import { InputDebounceDirective } from '../model/directives/input-debounce.directive';
import { FilterArrayPipe } from '../model/pipes/filter-array.pipe';
import { AchievementModalComponent } from './components/achievement-modal/achievement-modal.component';
import { AudioPlayerComponent } from './components/audio-player/audio-player.component';
import { ContentGroupComponent } from './components/content-group/content-group.component';
import { DeleteProfileModalComponent } from './components/delete-profile-modal/delete-profile-modal.component';
import { DownloadingOfflineModalComponent } from './components/downloading-offline-modal/downloading-offline-modal.component';
import { FullscreenComponent } from './components/fullscreen/fullscreen.component';
import { GoogleModelViewerComponent } from './components/google-model-viewer/google-model-viewer.component';
import { LanguageSwitcherComponent } from './components/language-switcher/language-switcher.component';
import { LoadMoreBtnComponent } from './components/load-more-btn/load-more-btn.component';
import { LoadingButtonComponent } from './components/loading-button/loading-button.component';
import { NotificationListDialogComponent } from './components/notification-list-dialog/notification-list-dialog.component';
import { NotificationListComponent } from './components/notification-list/notification-list.component';
import { NotificationPopupComponent } from './components/notification-popup/notification-popup.component';
import { PasswordInputComponent } from './components/password-input/password-input.component';
import { ZoomMeetingDialogComponent } from './components/zoom-meeting-dialog/zoom-meeting-dialog.component';
import { DateDiffPipe } from './pipes/date-diff.pipe';
import { LangFlagPipe } from './pipes/lang-flag.pipe';
import { MinutesToTimePipe } from './pipes/minutes-to-time.pipe';
import { NumberToLetterPipe } from './pipes/number-to-letter.pipe';
import { SecondsToMinutesPipe } from './pipes/seconds-to-minutes.pipe';
import { TimezoneAbbreviationPipe } from './pipes/timezone-abbreviation.pipe';
import { TrustUrlPipe } from './pipes/trust-url.pipe';
import { UserNamePipe } from './pipes/user-name.pipe';

import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { DisableOnOfflineDirective } from '@app/directives/disable-on-offline.directive';
import { ExtendedRouterLinkActiveDirective } from '@app/directives/extended-router-link-active.directive';
import { ModalImageDirective } from '@app/directives/modal-image.directive';
import { NgIfOfflineDirective } from '@app/directives/ng-if-offline.directive';
import { PanzoomDirective } from '@app/directives/panzoom.directive';
import { WatermarkDirective } from '@app/directives/watermark.directive';
import { NgSelectModule } from '@ng-select/ng-select';
import { CustomDatePipe, regexList, REGEX_LIST, SharedLibraryModule } from 'library-explorer';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgIfMediaQuery } from '../directives/ng-if-media-query.directive';
import { FullscreenProtectionOverlayComponent } from './components/fullscreen-protection-overlay/fullscreen-protection-overlay.component';
import { IconButtonComponent } from './components/icon-button/icon-button.component';
import { LanguageSelectDialogComponent } from './components/language-select-dialog/language-select-dialog.component';
import { ListLayoutSwitcherComponent } from './components/list-layout-switcher/list-layout-switcher.component';
import { ModalImageDialogComponent } from './components/modal-image-dialog/modal-image-dialog.component';
import { OptionToggleSwitchComponent } from './components/option-toggle-switch/option-toggle-switch.component';
import { SidebarMenuComponent } from './components/sidebar-menu/sidebar-menu.component';
import { WarningModalComponent } from './components/warning-modal/warning-modal.component';
import { EventCardComponent } from './components/event-card/event-card.component';
import { DurationPipe } from './pipes/duration.pipe';
import { ListEmptyStateComponent } from './components/list-empty-state/list-empty-state.component';

import { CardsListPlaceholderComponent } from './components/cards-list-placeholder/cards-list-placeholder.component';
import { CustomDateAdapter } from '@app/services/custom-date-adapter.service';
import { LiveUpdateModalComponent } from '@app/components/live-update-modal/live-update-modal.component';
import { DateLabelChipComponent } from './components/date-label-chip/date-label-chip.component';
import { DateToTimezonePipe } from './pipes/date-timezone.pipe';
import { CourseDetailsComponent } from './components/course-details/course-details.component';
import { CourseDetailsDialogComponent } from './components/course-details-dialog/course-details-dialog.component';
import { ModuleContentExpansionPanelComponent } from './components/module-content-expansion-panel/module-content-expansion-panel.component';
import { StepsProgressComponent } from './components/steps-progress/steps-progress.component';
import { NativeShowBiometricLoginDirective } from '@app/directives/native-biometric-login.directive';
import { BiometricAuthModalComponent } from '@app/components/biometric-auth-modal/biometric-auth-modal.component';
import { BiometricIconPipe } from './pipes/biometric-icon.pipe';
import { QuickSearchInputComponent } from './components/quick-search-input/quick-search-input.component';
import { AchievementCardComponent } from './components/achievement-card/achievement-card.component';
import { NativeNavigationBarComponent } from './components/native-navigation-bar/native-navigation-bar.component';
import { CertificateCardComponent } from './components/certificate-card/certificate-card.component';
import { CatalogPageBannerComponent } from './components/catalog-page-banner/catalog-page-banner.component';
import { SearchDialogComponent } from './components/search-dialog/search-dialog.component';
import { OpenSearchDialogDirective } from '@app/directives/open-search-dialog.directive';

import { CertificateModalComponent } from './components/certificate-modal/certificate-modal.component';
import { CertificatePreviewModalComponent } from './components/certificate-preview-modal/certificate-preview-modal.component';
import { NotificationsListDialogComponent } from './components/notifications-list-dialog/notifications-list-dialog.component';
import { RewardModalComponent } from './components/reward-modal/reward-modal.component';
import { CookieConsentButtonDirective } from '@app/directives/cookie-consent-button.directive';
import { HtmlContentInjectorComponent } from './components/html-content-injector/html-content-injector.component';
import { ParseHrefDirective } from '@app/directives/parse-href.directive';
import { TimeLeftPipe } from './pipes/time-left.pipe';
import { BadgeCouponCodeComponent } from './components/badge-coupon-code/badge-coupon-code.component';
import { LanguageLabelPipe } from './pipes/language-label.pipe';
import { NavigationElementDirective } from '@app/directives/navigation-element.directive';
import { NavigationItemsPipe } from './pipes/navigation-items.pipe';
import { CardLabelComponent } from './components/card-label/card-label.component';
import { NativeLinksClickDirective } from '@app/directives/native-link-click.directive';

const COMPONENTS = [
  LoadMoreBtnComponent,
  CardLabelComponent,
  BaseWarningModalComponent,
  LiveUpdateModalComponent,
  BiometricAuthModalComponent,
  AchievementModalComponent,
  SearchDialogComponent,
  LoadMoreBtnComponent,
  LanguageSwitcherComponent,
  FullscreenComponent,
  LoadMoreBtnComponent,
  LanguageSwitcherComponent,
  LoadingButtonComponent,
  DeleteProfileModalComponent,
  AudioPlayerComponent,
  NotificationListDialogComponent,
  NotificationListComponent,
  NotificationPopupComponent,
  PasswordInputComponent,
  DownloadingOfflineModalComponent,
  ContentGroupComponent,
  ZoomMeetingDialogComponent,
  GoogleModelViewerComponent,
  WarningModalComponent,
  ListLayoutSwitcherComponent,
  FullscreenProtectionOverlayComponent,
  EventCardComponent,
  ModalImageDialogComponent,
  LanguageSelectDialogComponent,
  OptionToggleSwitchComponent,
  SidebarMenuComponent,
  IconButtonComponent,
  ListEmptyStateComponent,
  CardsListPlaceholderComponent,
  DateLabelChipComponent,
  CourseDetailsComponent,
  CourseDetailsDialogComponent,
  ModuleContentExpansionPanelComponent,
  StepsProgressComponent,
  QuickSearchInputComponent,
  AchievementCardComponent,
  NativeNavigationBarComponent,
  CertificateCardComponent,
  CatalogPageBannerComponent,
  CertificateModalComponent,
  CertificatePreviewModalComponent,
  NotificationsListDialogComponent,
  HtmlContentInjectorComponent,
  RewardModalComponent,
  BadgeCouponCodeComponent,
];

const DIRECTIVES = [
  HighlightDirective,
  InputDebounceDirective,
  DisableControlDirective,
  ExtendedRouterLinkDirective,
  DisableCopyPasteDirective,
  TripleClickDirective,
  NgIfMediaQuery,
  PanzoomDirective,
  ModalImageDirective,
  OptionToggleSwitchComponent,
  WatermarkDirective,
  NgIfOfflineDirective,
  DisableOnOfflineDirective,
  NativeShowBiometricLoginDirective,
  NativeLinksClickDirective,
  ExtendedRouterLinkActiveDirective,
  OpenSearchDialogDirective,
  CookieConsentButtonDirective,
  ParseHrefDirective,
  NavigationElementDirective
];

const PIPES = [
  FilterArrayPipe,
  SecondsToMinutesPipe,
  TrustUrlPipe,
  LangFlagPipe,
  UserNamePipe,
  NumberToLetterPipe,
  DateDiffPipe,
  MinutesToTimePipe,
  TimezoneAbbreviationPipe,
  BiometricIconPipe,
  DurationPipe,
  DateToTimezonePipe,
  TimeLeftPipe,
  LanguageLabelPipe,
  NavigationItemsPipe
];

const ANGULAR_MODULES = [
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  RouterModule
];

const OTHER_MODULES = [
  MatProgressSpinnerModule,
  MatProgressBarModule,
  MatIconModule,
  MatCardModule,
  MatButtonModule,
  MatInputModule,
  MatFormFieldModule,
  MatCheckboxModule,
  MatToolbarModule,
  MatSelectModule,
  MatMenuModule,
  MatListModule,
  MatDialogModule,
  MatExpansionModule,
  MatTooltipModule,
  MatRadioModule,
  MatSliderModule,
  DragDropModule,
  NgScrollbarModule,
  AngularSvgIconModule,
  NgClickOutsideDirective,
  EllipsisModule,
  OverlayModule,
  NgxPaginationModule,
  CodeInputModule,
  NgSelectModule,
  SharedLibraryModule,
  TranslateModule,
  MatTabsModule
];

export function scrollFactory(overlay: Overlay): () => CloseScrollStrategy | RepositionScrollStrategy {
  return () => {
    const isPhone = window.matchMedia('(max-width: 768px)').matches;

    return isPhone ? overlay.scrollStrategies.close() : overlay.scrollStrategies.reposition();
  };
}

const PROVIDERS = [
  {
    provide: NG_SCROLLBAR_OPTIONS,
    useValue: {
      scrollAuditTime: 20
    }
  },
  DatePipe,
  CustomDatePipe,
  { provide: REGEX_LIST, useValue: regexList },
  { provide: MAT_SELECT_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] },
  {
    provide: LOCALE_ID,
    deps: [TranslateService],
    useFactory: (translateService: TranslateService) => translateService.currentLang || translateService.defaultLang
  },
  {
    provide: MAT_DATE_LOCALE,
    deps: [TranslateService],
    useFactory: (translateService: TranslateService) => translateService.currentLang || translateService.defaultLang
  },
  {
    provide: DateAdapter,
    useClass: CustomDateAdapter
  }
];

@NgModule({
  declarations: [
    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES
  ],
  imports: [
    ...ANGULAR_MODULES,
    ...OTHER_MODULES
  ],
  exports: [
    ...ANGULAR_MODULES,
    ...OTHER_MODULES,
    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES
  ],
  providers: [
    ...PROVIDERS,
    provideScrollbarOptions({
      orientation: 'auto'
    })
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedModule {
}
